<script setup lang="ts">
import { computed, ref, watchEffect, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import { toast } from "@/common/utils/toast";

import {
  analysisFormList,
  analysisFormCreate,
  analysisFormDelete,
} from "@/common/api/rest/analysis/form";

import type { AnalysisListType } from "@/common/types/analysis";
import type { FormType } from "@/common/types/form";

import { searchForm } from "@/components";
import {
  baseConfirm,
  baseModal,
  baseTooltip,
  svgIcon,
} from "@/common/components";

const { t } = useI18n();

const route = useRoute();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: AnalysisListType;
    width?: string;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    isEdit: true,
  },
);

const selected = ref<any>(null);
const forms = ref<any[]>([]);

const showModalAdd = ref(false);
const showDelConfirm = ref(false);

const accountId = computed(() => route.params.id as string);

function fetchForm() {
  if (!props.data) return;

  analysisFormList(props.data.account_id, {
    analysis_id: props.data.analysis_id,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        forms.value = data.list;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function add() {
  showModalAdd.value = true;
}

function addCancel() {
  showModalAdd.value = false;
}

function addSelect(data: FormType) {
  selected.value = data;
}

function addConfirm() {
  showModalAdd.value = false;

  analysisFormCreate(props.data.account_id, {
    evaluation_id: props.data.evaluation_id,
    analysis_id: props.data.analysis_id,
    form_id: selected.value.form_id,
    form_name: selected.value.code,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        emit("update-data");
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function del(item: FormType) {
  selected.value = item;
  showDelConfirm.value = true;
}

function delCancel() {
  selected.value = null;
  showDelConfirm.value = false;
}

function delConfirm() {
  showDelConfirm.value = false;

  analysisFormDelete(props.data.account_id, selected.value.item_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        emit("update-data");
        delCancel();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
  delCancel();
}

watchEffect(() => {
  fetchForm();
});

onMounted(() => {
  fetchForm();
});
</script>

<template>
  <section class="analyse-tab" :class="{ width: width }">
    <div class="content">
      <template v-if="forms.length === 0">
        <div class="form-empty">
          {{ t("analysis.texts.empty_form") }}
        </div>
      </template>

      <template v-else>
        <template v-for="(item, index) in forms" :key="index">
          <div class="header">
            <div class="infos">
              <div class="title">
                {{ item.code }}
              </div>
              <div class="subtitle">
                {{ item.name }}
              </div>
            </div>
            <div class="action">
              <base-tooltip>
                <template #trigger>
                  <button
                    type="button"
                    class="btn btn-none btn-sm remove"
                    @click="del(item)"
                  >
                    <svg-icon icon="trash" size="1.75rem" />
                  </button>
                </template>
                <template #tooltip>
                  {{ t("common.labels.delete") }}
                </template>
              </base-tooltip>
            </div>
          </div>
        </template>
      </template>

      <div class="actions">
        <button class="btn btn-md btn-primary" @click="add()">
          {{ t("common.labels.add").toUpperCase() }}
        </button>
      </div>
    </div>

    <base-modal
      :show="showModalAdd"
      :title="
        t('common.labels.add', {
          concat: t('common.labels.form'),
        }).toUpperCase()
      "
      :show-button-confirm="true"
      :button-confirm="t('common.labels.add')"
      :button-close="t('common.labels.cancel')"
      @close="addCancel()"
      @confirm="addConfirm()"
    >
      <template #content>
        <search-form
          width="600px"
          :account-id="accountId"
          :ids="forms.map((item) => item.form_id)"
          @update-data="addSelect"
        />
      </template>
    </base-modal>

    <base-confirm
      :show="showDelConfirm"
      max-width="400px"
      :title="
        t('common.labels.remove', {
          concat: t('common.labels.form'),
        }).toUpperCase()
      "
      :message="
        t('common.texts.remove_message', {
          concat: t('common.labels.form'),
        })
      "
      @cancel="delCancel"
      @confirm="delConfirm"
    />
  </section>
</template>

<style lang="scss" scoped>
.analyse-tab {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-block-start: 1rem;
  margin-block-end: 2rem;
  padding-inline: 1.54rem;

  .header {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    padding-block: 1rem;
    border-bottom: 1px solid var(--border);

    .infos {
      flex: 3;
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      margin-block-end: 0.5rem;

      .title {
        font-size: 1.15rem;
        font-weight: bold;
      }

      .subtitle {
        font-size: 1rem;
      }
    }

    .action {
      width: auto;

      button {
        &:hover {
          color: var(--red);
        }
      }
    }
  }

  .actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding-block: 1.5rem;
  }

  .form-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    padding-block: 1.5rem;
  }
}
</style>
